import { FC, LegacyRef, MouseEvent, useState } from 'react';
import { PAYOUT_TXNS_TABS } from '../constants';
import {
  ArrowRightIcon,
  ExportIcon,
  FilterIcon,
  NavCloseIcon,
  NoSearchResultsIcon,
  SearchIconWithBorder
} from 'assets/icons';
import { VoidFn } from 'types/baseTypes';
import { CustomButton, If, InfiniteLoadingSpinner, NoPermissions, SearchBar } from 'components';
import { PayoutTxnsData } from '../payouts.model';
import PayoutTransactionCard from './PayoutTransactionCard';
import PayoutTabGroup from './PayoutTabGroup';
import PayoutTab from './PayoutTab';

interface PayoutTransactionsProps {
  payoutId: string;
  handleSidePanelClose: VoidFn;
  setIntersectionElement: LegacyRef<HTMLDivElement>;
  isLoadingTxns: boolean;
  hasMoreTxns: boolean;
  payoutTxns: Record<string, PayoutTxnsData[]>;
  isLoadingSuccess: boolean;
  totalCount: number;
  onFilterClick: VoidFn;
  selectedTab: PAYOUT_TXNS_TABS;
  setSelectedTab: React.Dispatch<React.SetStateAction<PAYOUT_TXNS_TABS>>;
  isTxnsError: boolean;
  searchText: string;
  handleSearchTermChange: (term: string) => void;
  onClearSearch;
  hasPermissionsToView: boolean;
  onExportClick: VoidFn;
  filterCount: number;
  clearFilters: (e: MouseEvent) => void;
}

const PayoutTransactions: FC<PayoutTransactionsProps> = ({
  payoutId,
  handleSidePanelClose,
  setIntersectionElement,
  isLoadingTxns,
  hasMoreTxns,
  payoutTxns,
  isLoadingSuccess,
  totalCount,
  onFilterClick,
  selectedTab,
  setSelectedTab,
  searchText,
  onClearSearch,
  handleSearchTermChange,
  hasPermissionsToView,
  onExportClick,
  filterCount,
  clearFilters
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const onSearchTermChange = (text: string) => {
    handleSearchTermChange(text);
  };

  const changeTabs = (tab: PAYOUT_TXNS_TABS) => {
    if (tab === selectedTab) return;
    setSelectedTab(tab);
  };

  return (
    <div className="h-full w-96 py-5">
      <div className="flex items-center gap-2 px-3.5">
        <div onClick={handleSidePanelClose} className="cursor-pointer">
          <ArrowRightIcon className="rotate-180" />
        </div>
        <div className="text-17px font-semibold text-headingGray">Transactions {payoutId}</div>
      </div>
      {hasPermissionsToView ? (
        <div className="customNormalScroll h-full overflow-auto">
          <PayoutTabGroup className="mx-[30px] my-5">
            <PayoutTab
              label="Payments"
              isActive={selectedTab === PAYOUT_TXNS_TABS.PAYMENTS}
              count={totalCount}
              onClick={() => changeTabs(PAYOUT_TXNS_TABS.PAYMENTS)}
              showCount={selectedTab === PAYOUT_TXNS_TABS.PAYMENTS}
              isLoadingSuccess={isLoadingSuccess}
              isLoadingData={isLoadingTxns}
            />
            <PayoutTab
              label="All"
              isActive={selectedTab === PAYOUT_TXNS_TABS.ALL}
              count={totalCount}
              onClick={() => changeTabs(PAYOUT_TXNS_TABS.ALL)}
              showCount={selectedTab === PAYOUT_TXNS_TABS.ALL}
              isLoadingSuccess={isLoadingSuccess}
              isLoadingData={isLoadingTxns}
            />
          </PayoutTabGroup>
          <>
            <div className="mx-[30px] flex flex-wrap justify-between gap-2.5">
              {isSearchExpanded || searchText ? (
                <SearchBar
                  id="payouts-txns-search"
                  handleClear={onClearSearch}
                  handleChange={onSearchTermChange}
                  value={searchText}
                  placeholder="Search with Receipt id or Dispute id"
                  wrapperStyle={'w-full h-10 border border-borderGray rounded'}
                  showSearchIcon={true}
                  autoFocus={false}
                  showCrossIcon={true}
                />
              ) : (
                <div onClick={handleSearchIconClick}>
                  <SearchIconWithBorder className="flex h-9 cursor-pointer" />
                </div>
              )}
              <CustomButton
                onClick={onFilterClick}
                id="payout-txn-filter-button"
                StartIcon={FilterIcon}
                type={null}
                className="h-9 border border-borderGray 
                   bg-primary p-2.5 text-sbase font-normal text-tertiaryText">
                <div className="flex items-center">
                  Filter
                  {filterCount > 0 && (
                    <>
                      <span
                        className="ml-1 rounded-[10px] bg-[#F7C0C0] px-2.5 
                    text-px13 font-semibold text-primaryText">
                        {filterCount}
                      </span>
                      <span className="ml-2 cursor-pointer" onClick={clearFilters}>
                        <NavCloseIcon width={10} height={10} />
                      </span>
                    </>
                  )}
                </div>
              </CustomButton>
              <CustomButton
                onClick={onExportClick}
                id="payout-txn-export-button"
                StartIcon={ExportIcon}
                type={null}
                className="ml-auto h-9 border border-borderGray
                   bg-primary p-2.5 text-sbase font-normal text-tertiaryText">
                Export
              </CustomButton>
            </div>
            <div>
              <If condition={totalCount > 0}>
                {Object.keys(payoutTxns).map(date => {
                  return (
                    <div className="my-5" key={date}>
                      <div className="bg-secondaryBtn py-2.5 px-[30px] text-sbase text-accentText">{date}</div>
                      {payoutTxns[date].map(txn => (
                        <div key={txn.ledgerId} className="mx-[30px] border-b border-borderGray py-4 ">
                          <PayoutTransactionCard data={txn} />
                        </div>
                      ))}
                    </div>
                  );
                })}
              </If>
              <If condition={!isLoadingTxns && hasMoreTxns}>
                <div className="h-2" ref={setIntersectionElement}></div>
              </If>
              {isLoadingTxns && (
                <div className="pt-2">
                  <InfiniteLoadingSpinner />
                </div>
              )}
              {isLoadingSuccess && !isLoadingTxns && totalCount === 0 && (
                <div className="mt-40 flex h-full flex-col items-center justify-center gap-4 text-center">
                  <NoSearchResultsIcon height={24} width={24} />
                  <div className="text-sbase text-primaryText">
                    <div className="font-bold ">No transactions to show</div>
                    Try changing the filters or switch tabs.
                  </div>
                </div>
              )}
            </div>
          </>
        </div>
      ) : (
        <NoPermissions />
      )}
    </div>
  );
};

export default PayoutTransactions;
